import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from './ui/select';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { toast, Toaster } from 'sonner';
import { apiCall } from '../config/api';
import { supabase } from '../config/supabase';
import {
  Fish,
  Camera,
  Shield,
  Zap,
  Gauge,
  Factory,
  ChevronRight,
  Calendar,
  MapPin,
  Users,
  TrendingUp,
  CheckCircle,
  QrCode,
  Play,
  ArrowRight,
  Waves,
  Brain,
  Target,
  Clock,
  Database,
  Wifi,
  WifiOff,
  Settings,
  BarChart3
} from 'lucide-react';

// Form validation schema
const contactFormSchema = z.object({
  nombre: z.string().min(2, { message: "Nombre requerido" }),
  cargo: z.string().min(2, { message: "Cargo requerido" }),
  empresa: z.string().min(2, { message: "Nombre de empresa requerido" }),
  correo: z.string().email({ message: "Email válido requerido" }),
  numero: z.string().optional(),
  descripcion: z.string().optional()
});

const InnAqua2025 = () => {
  const [selectedUseCase, setSelectedUseCase] = useState(null);
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  // Initialize form with react-hook-form and zod validation
  const form = useForm({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      nombre: '',
      cargo: '',
      empresa: '',
      correo: '',
      numero: '',
      descripcion: ''
    }
  });

  // Scroll animation effect
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  // Use cases data
  const useCases = [
    {
      id: 1,
      icon: Fish,
      title: "Alimentación Inteligente",
      subtitle: "IA para optimización de feeding",
      description: "Detectamos automáticamente pellets y boquedos usando las cámaras CCTV existentes, ajustando la tasa de alimentación en tiempo real para maximizar la conversión alimenticia.",
      benefits: ["Optimización del desperdicio de alimento", "Mejora en la conversión alimenticia", "Alertas automáticas de boquedos"],
      tech: "Computer Vision + Deep Learning"
    },
    {
      id: 2,
      icon: Shield,
      title: "Salud y Comportamiento",
      subtitle: "Detección temprana de anomalías",
      description: "Análisis continuo de patrones de nado y concentración de peces para identificar alertas tempranas de enfermedades, estrés o problemas ambientales.",
      benefits: ["Detección temprana antes de síntomas", "Mejora en la supervivencia", "Optimización de tratamientos"],
      tech: "ML + Análisis comportamental"
    },
    {
      id: 3,
      icon: Camera,
      title: "Inspección de Infraestructura",
      subtitle: "CV para redes y pontones",
      description: "Inspección automática mediante computer vision para detectar daños, desgaste o ensuciamiento en redes, pontones y estructuras submarinas.",
      benefits: ["Inspección más eficiente", "Detección temprana de daños", "Mantenimiento predictivo inteligente"],
      tech: "Computer Vision + Drones/ROV"
    },
    {
      id: 4,
      icon: Waves,
      title: "Predicción Oceanográfica",
      subtitle: "Modelos FAN y oxígeno",
      description: "Modelos predictivos que combinan datos meteorológicos, oceanográficos y de sondas para anticipar eventos de FAN, bajas de oxígeno y cambios críticos.",
      benefits: ["Predicción FAN 5-7 días", "Alertas oxígeno 24-48h", "Optimización cosechas"],
      tech: "ML + IoT + Datos satelitales"
    },
    {
      id: 5,
      icon: Zap,
      title: "Optimización Energética",
      subtitle: "Gemelo digital + forecasting",
      description: "Forecasting de consumo energético y gemelo digital de operaciones para optimizar eficiencia, integrar renovables y mejorar la sostenibilidad operacional.",
      benefits: ["Optimización del consumo energético", "Integración de energías renovables", "Predicción inteligente de demanda"],
      tech: "Digital Twin + ML + IoT"
    },
    {
      id: 6,
      icon: Factory,
      title: "Control de Calidad",
      subtitle: "Inspección por imagen en planta",
      description: "Sistema de computer vision para control de calidad automático en planta: detección de color, manchas, espinas y clasificación por tamaños en línea de proceso.",
      benefits: ["Alta precisión en clasificación", "Automatización de la inspección", "Trazabilidad completa del proceso"],
      tech: "Computer Vision + Edge Computing"
    }
  ];


  // Handle form submission
  const onSubmit = async (data) => {
    setIsFormSubmitting(true);
    
    // Show loading toast
    const loadingToast = toast.loading("Enviando tu mensaje...", {
      description: "Por favor espera un momento",
    });
    
    try {
      // Prepare contact data for Supabase
      const currentDate = new Date().toISOString();
      const contactData = {
        name: data.nombre,
        email: data.correo,
        phone: data.numero || null,
        company: data.empresa,
        subject: `[INNAQUA] ${data.nombre} - ${data.cargo}`,
        message: `CONTACTO INNAQUA 2025:\n\nINFORMACIÓN DE CONTACTO:\n- Nombre: ${data.nombre}\n- Cargo: ${data.cargo}\n- Empresa: ${data.empresa}\n- Email: ${data.correo}${data.numero ? `\n- Teléfono: ${data.numero}` : ''}${data.descripcion ? `\n\nDESCRIPCIÓN:\n${data.descripcion}` : ''}\n\nFecha de envío: ${currentDate}`,
        status: 'innaqua_contact'
      };

      let result;
      
      // Try to use backend API first (for local development)
      if (window.location.hostname === 'localhost') {
        try {
          const payload = {
            nombre: data.nombre,
            cargo: data.cargo,
            empresa: data.empresa,
            correo: data.correo,
            numero: data.numero,
            descripcion: data.descripcion
          };
          
          result = await apiCall('/api/innaqua-contact', {
            method: 'POST',
            body: JSON.stringify(payload)
          });
          console.log('Contact form submitted via API:', result);
        } catch (apiError) {
          console.log('API call failed, falling back to direct Supabase:', apiError);
          // Fallback to direct Supabase
          const { data: supabaseData, error } = await supabase
            .from('contacts')
            .insert([contactData])
            .select();
          
          if (error) throw error;
          result = { data: supabaseData };
          console.log('Contact form submitted via Supabase:', result);
        }
      } else {
        // In production, use Supabase directly
        const { data: supabaseData, error } = await supabase
          .from('contacts')
          .insert([contactData])
          .select();
        
        if (error) throw error;
        result = { data: supabaseData };
        console.log('Contact form submitted via Supabase:', result);
      }
      
      // Dismiss loading toast
      toast.dismiss(loadingToast);
      
      // Show success message with custom styling
      toast.success("Mensaje enviado exitosamente", {
        description: "Hemos recibido tu solicitud. Nuestro equipo se pondrá en contacto contigo en las próximas 24-48 horas.",
        duration: 6000,
        style: {
          background: '#10b981',
          color: 'white',
        },
      });
      
      // Reset form after successful submission
      form.reset();
      
      // Optionally scroll to top to show the success message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Error submitting contact form:', error);
      // Dismiss loading toast on error
      toast.dismiss(loadingToast);
      
      toast.error("Error al enviar el mensaje", {
        description: error.message || "Por favor verifica tu conexión e intenta nuevamente. Si el problema persiste, contáctanos directamente al correo: hola@neurolytics.cl",
        duration: 6000,
        style: {
          background: '#ef4444',
          color: 'white',
        },
      });
    } finally {
      setIsFormSubmitting(false);
    }
  };


  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-teal-50">
      {/* Hero Section */}
      <section className={`relative overflow-hidden transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 via-teal-600/10 to-cyan-600/10" />
        <div className="relative container mx-auto px-4 py-16 sm:py-24">
          <div className="text-center space-y-8">
            {/* Event Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
              <Calendar className="h-4 w-4" />
              <span>2-4 Septiembre 2025</span>
              <MapPin className="h-4 w-4 ml-2" />
              <span>Puerto Montt - Puerto Varas</span>
            </div>

            {/* Main Headline */}
            <div className="space-y-6">
              <div className="space-y-2">
                <p className="text-xl sm:text-2xl font-semibold text-slate-600">
                  Neurolytics presente en
                </p>
                <h1 className="text-5xl sm:text-7xl lg:text-8xl font-bold bg-gradient-to-r from-blue-600 via-teal-600 to-cyan-600 bg-clip-text text-transparent leading-tight">
                  InnAqua 2025
                </h1>
              </div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-slate-800 max-w-4xl mx-auto">
                Optimizamos operaciones y reducimos riesgos con <span className="text-teal-600">IA y visión por computador</span>
              </h2>
            </div>

            {/* 4 Pillars */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 max-w-4xl mx-auto mt-12">
              {[
                { icon: Zap, text: "Energía Renovable", color: "text-yellow-600" },
                { icon: Waves, text: "Huella de Carbono", color: "text-blue-600" },
                { icon: Target, text: "Economía Circular", color: "text-green-600" },
                { icon: Users, text: "Innovación Social", color: "text-purple-600" }
              ].map((pillar, index) => (
                <Card key={index} className="p-4 border-2 hover:border-teal-300 transition-all duration-300 hover:scale-105">
                  <div className="flex flex-col items-center text-center space-y-2">
                    <pillar.icon className={`h-8 w-8 ${pillar.color}`} />
                    <span className="text-sm font-medium text-slate-700">{pillar.text}</span>
                  </div>
                </Card>
              ))}
            </div>

            {/* CTA Button */}
            <div className="pt-8">
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700 text-white px-8 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
                onClick={() => document.getElementById('contact-form').scrollIntoView({ behavior: 'smooth' })}
              >
                Contáctate con nosotros
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-800 mb-4">
              6 Casos de Uso Probados en Acuicultura
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Soluciones de IA específicas para cada etapa del proceso productivo, 
              desde mar hasta planta, con resultados medibles en 4-6 semanas.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {useCases.map((useCase, index) => {
              const IconComponent = useCase.icon;
              return (
                <Card 
                  key={useCase.id} 
                  className={`group cursor-pointer transition-all duration-500 hover:scale-105 hover:shadow-xl border-2 hover:border-teal-300 ${
                    index % 2 === 0 ? 'animate-fade-in-up' : 'animate-fade-in-down'
                  }`}
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <CardHeader className="pb-4">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="p-2 bg-gradient-to-r from-blue-500 to-teal-500 rounded-lg text-white">
                        <IconComponent className="h-6 w-6" />
                      </div>
                      <Badge variant="secondary" className="text-xs">
                        {useCase.tech}
                      </Badge>
                    </div>
                    <CardTitle className="text-xl font-bold text-slate-800 group-hover:text-teal-600 transition-colors">
                      {useCase.title}
                    </CardTitle>
                    <CardDescription className="text-sm font-medium text-teal-600">
                      {useCase.subtitle}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <p className="text-slate-600 mb-4 line-clamp-3">
                      {useCase.description}
                    </p>
                    <div className="space-y-3">
                      <div className="flex flex-wrap gap-1">
                        {useCase.benefits.slice(0, 2).map((benefit, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">
                            <CheckCircle className="h-3 w-3 mr-1 text-green-500" />
                            {benefit}
                          </Badge>
                        ))}
                      </div>
                      <div className="flex items-center justify-end">
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-teal-600 hover:text-teal-700 hover:bg-teal-50"
                            >
                              Ver más
                              <ChevronRight className="ml-1 h-4 w-4" />
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-2xl">
                            <DialogHeader>
                              <DialogTitle className="flex items-center gap-3">
                                <div className="p-2 bg-gradient-to-r from-blue-500 to-teal-500 rounded-lg text-white">
                                  <IconComponent className="h-6 w-6" />
                                </div>
                                {useCase.title}
                              </DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4">
                              <p className="text-slate-600">{useCase.description}</p>
                              <Separator />
                              <div>
                                <h4 className="font-semibold text-slate-800 mb-2">Beneficios Clave:</h4>
                                <ul className="space-y-1">
                                  {useCase.benefits.map((benefit, idx) => (
                                    <li key={idx} className="flex items-center gap-2 text-slate-600">
                                      <CheckCircle className="h-4 w-4 text-green-500" />
                                      {benefit}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                              <Separator />
                              <div className="flex items-center justify-center">
                                <Badge variant="secondary">{useCase.tech}</Badge>
                              </div>
                            </div>
                          </DialogContent>
                        </Dialog>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact-form" className="py-20 px-4 bg-white/50">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-800 mb-4">
              Contáctate con nosotros
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Completa este formulario y nos pondremos en contacto contigo 
              para discutir cómo podemos ayudarte a optimizar tus operaciones.
            </p>
          </div>

          <Card className="border-2 border-teal-100 shadow-lg">
            <CardHeader className="pb-6">
              <CardTitle className="text-2xl text-center text-slate-800">
                Formulario de Contacto
              </CardTitle>
              <CardDescription className="text-center">
                Cuéntanos sobre tu empresa y cómo podemos ayudarte
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                {/* Personal Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="nombre">Nombre *</Label>
                    <Input
                      id="nombre"
                      placeholder="Ej: Juan Pérez"
                      {...form.register('nombre')}
                      className={form.formState.errors.nombre ? 'border-red-500' : ''}
                    />
                    {form.formState.errors.nombre && (
                      <p className="text-red-500 text-sm">{form.formState.errors.nombre.message}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cargo">Cargo *</Label>
                    <Input
                      id="cargo"
                      placeholder="Ej: Gerente de Operaciones"
                      {...form.register('cargo')}
                      className={form.formState.errors.cargo ? 'border-red-500' : ''}
                    />
                    {form.formState.errors.cargo && (
                      <p className="text-red-500 text-sm">{form.formState.errors.cargo.message}</p>
                    )}
                  </div>
                </div>
                
                {/* Company Information */}
                <div className="space-y-2">
                  <Label htmlFor="empresa">Empresa *</Label>
                  <Input
                    id="empresa"
                    placeholder="Ej: Salmones Austral"
                    {...form.register('empresa')}
                    className={form.formState.errors.empresa ? 'border-red-500' : ''}
                  />
                  {form.formState.errors.empresa && (
                    <p className="text-red-500 text-sm">{form.formState.errors.empresa.message}</p>
                  )}
                </div>

                {/* Contact Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="correo">Correo electrónico *</Label>
                    <Input
                      id="correo"
                      type="email"
                      placeholder="Ej: juan.perez@empresa.cl"
                      {...form.register('correo')}
                      className={form.formState.errors.correo ? 'border-red-500' : ''}
                    />
                    {form.formState.errors.correo && (
                      <p className="text-red-500 text-sm">{form.formState.errors.correo.message}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="numero">Número de teléfono (opcional)</Label>
                    <Input
                      id="numero"
                      placeholder="Ej: +56 9 1234 5678"
                      {...form.register('numero')}
                      className={form.formState.errors.numero ? 'border-red-500' : ''}
                    />
                    {form.formState.errors.numero && (
                      <p className="text-red-500 text-sm">{form.formState.errors.numero.message}</p>
                    )}
                  </div>
                </div>

                {/* Description */}
                <div className="space-y-2">
                  <Label htmlFor="descripcion">Descripción (opcional)</Label>
                  <Textarea
                    id="descripcion"
                    placeholder="Cuéntanos sobre tu proyecto o necesidades. ¿Cómo podemos ayudarte a optimizar tus operaciones?"
                    rows={4}
                    {...form.register('descripcion')}
                    className={form.formState.errors.descripcion ? 'border-red-500' : ''}
                  />
                  {form.formState.errors.descripcion && (
                    <p className="text-red-500 text-sm">{form.formState.errors.descripcion.message}</p>
                  )}
                </div>

                {/* Submit Button */}
                <div className="pt-6">
                  <Button 
                    type="submit" 
                    size="lg" 
                    className="w-full bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700 text-white py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
                    disabled={isFormSubmitting}
                  >
                    {isFormSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        Enviando Mensaje...
                      </>
                    ) : (
                      <>
                        Enviar Mensaje
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* QR Code Section */}
      <section className="py-16 px-4 bg-gradient-to-r from-blue-600 to-teal-600 text-white">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto space-y-8">
            {/* Header */}
            <div className="text-center space-y-4">
              <Badge className="bg-white/20 text-white border-white/30 px-4 py-2 text-lg">
                CITT - Centro de Innovación y Transferencia Tecnológica
              </Badge>
              <h2 className="text-3xl sm:text-4xl font-bold">
                Visítanos en el Stand de Duoc UC
              </h2>
              <p className="text-xl text-blue-100 max-w-2xl mx-auto">
                Neurolytics es parte del ecosistema de innovación del CITT Duoc UC, 
                liderando los tracks de Robótica e Inteligencia Artificial
              </p>
            </div>
            
            {/* QR and Video Info */}
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <Card className="bg-white p-8 shadow-2xl">
                <div className="space-y-4">
                  <QrCode className="h-40 w-40 mx-auto text-slate-400" />
                  <div className="text-center">
                    <p className="text-slate-600 font-medium">Escanea para conocer</p>
                    <p className="text-lg font-bold text-slate-800">Video del CITT Duoc UC</p>
                    <p className="text-sm text-slate-500 mt-2">
                      Centro de Innovación y Transferencia Tecnológica
                    </p>
                  </div>
                </div>
              </Card>

              {/* Leaders Info */}
              <div className="space-y-6">
                <h3 className="text-2xl font-bold text-white">
                  Líderes de Track del CITT
                </h3>
                
                <div className="space-y-4">
                  {/* Luis Salamanca - AI */}
                  <Card className="bg-white/10 backdrop-blur border-white/20 p-4">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-white/20 rounded-lg">
                        <Brain className="h-6 w-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-bold text-white">Luis Salamanca</h4>
                        <p className="text-blue-100">Líder Track de Inteligencia Artificial</p>
                        <div className="flex gap-3 mt-2 text-sm">
                          <a 
                            href="mailto:l.salamanca18@icloud.com" 
                            className="text-blue-200 hover:text-white transition-colors"
                          >
                            Email
                          </a>
                          <a 
                            href="https://link.me/luissalamanca" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-blue-200 hover:text-white transition-colors"
                          >
                            Perfil
                          </a>
                        </div>
                      </div>
                    </div>
                  </Card>

                  {/* Joaquin Calderon - Robotics */}
                  <Card className="bg-white/10 backdrop-blur border-white/20 p-4">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-white/20 rounded-lg">
                        <Settings className="h-6 w-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-bold text-white">Joaquín Calderón</h4>
                        <p className="text-blue-100">Líder Track de Robótica</p>
                        <div className="flex gap-3 mt-2 text-sm">
                          <a 
                            href="mailto:joa.calderonm@duocuc.cl" 
                            className="text-blue-200 hover:text-white transition-colors"
                          >
                            Email
                          </a>
                        </div>
                      </div>
                    </div>
                  </Card>
                </div>
              </div>
            </div>

            {/* Features */}
            <div className="flex flex-wrap items-center justify-center gap-6 text-blue-100">
              <div className="flex items-center gap-2">
                <Target className="h-5 w-5" />
                <span>Proyectos de innovación</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                <span>Colaboración academia-industria</span>
              </div>
              <div className="flex items-center gap-2">
                <Zap className="h-5 w-5" />
                <span>Transferencia tecnológica</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Event Info Footer */}
      <section className="py-12 px-4 bg-slate-900 text-white">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div className="space-y-2">
              <Calendar className="h-8 w-8 mx-auto text-teal-400" />
              <h3 className="font-semibold text-lg">Fechas del Evento</h3>
              <p className="text-slate-300">2-4 Septiembre 2025</p>
            </div>
            <div className="space-y-2">
              <MapPin className="h-8 w-8 mx-auto text-teal-400" />
              <h3 className="font-semibold text-lg">Ubicación</h3>
              <p className="text-slate-300">Puerto Montt - Puerto Varas</p>
            </div>
            <div className="space-y-2">
              <Users className="h-8 w-8 mx-auto text-teal-400" />
              <h3 className="font-semibold text-lg">Audiencia</h3>
              <p className="text-slate-300">Productores, Proveedores Tech, Asociaciones</p>
            </div>
          </div>
          
          <div className="text-center mt-12 pt-8 border-t border-slate-700">
            <div className="space-y-2">
              <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-teal-400 bg-clip-text text-transparent">
                Neurolytics
              </h2>
              <p className="text-slate-300">IA y Visión por Computador para Acuicultura</p>
              <p className="text-sm text-slate-400">Optimizando operaciones y reduciendo riesgos con tecnología probada</p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Toast Notifications */}
      <Toaster 
        position="top-center"
        richColors
        closeButton
        duration={5000}
      />
    </div>
  );
};

export default InnAqua2025;